import { FlagModel, ValueModel } from '@zajno/common-mobx/viewModels';
import { CampaignViewModel } from '../components/CampaignViewModel';
import * as Yup from 'yup';
import { AppController } from 'app/controllers';
import CheckboxViewModel from '../components/CheckboxViewModel';
import { PromotedSong } from 'components/sections/checkout/checkout/viewModel';
import { LinksRequestDto } from 'common/types/dtos/links';
import { createLogger } from '@zajno/common/logger';
import { ArtistInfoData, AssetLinkData } from 'components/sections/checkout/checkout/viewModel';
import { AppViewModel } from '..';
import { UpdatePlaylistsDto } from 'common/types/dtos/updatePlaylists';
import { CampaignMainInfoUpdateData } from 'common/types/models/campaign';

const logger = createLogger('[CampaignInnerPageVM]');

export type CampaignPlaylistsAddData= {
    playlist: string,
};

export const updateCampaignInfoValidationSchema: Yup.ObjectSchema<CampaignMainInfoUpdateData> = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string().email("It's not a valid email").required('Email required'),
    guaranteedStream: Yup.number().required('Password required'),
});

export const updatePlaylistsValidationSchema: Yup.ObjectSchema<CampaignPlaylistsAddData> = Yup.object().shape({
    playlist: Yup.string().matches(UpdatePlaylistsDto.spotifyPlaylistRegex, 'Invalid spotify playlist link').required('Playlist is required'),
});

export class CampaignInnerPageViewModel {
    private _campaignId: string;
    public campaignModel: CampaignViewModel;

    private readonly _isLoading = new FlagModel(false);
    public readonly mainInfoModalModel = new FlagModel(false);
    public readonly songModalModel = new FlagModel(false);
    public readonly addPlaylistModalModel = new FlagModel(false);

    private readonly _searchedSong = new ValueModel<PromotedSong>(null);
    private readonly _checkboxIsNotReleasedModel = new CheckboxViewModel();

    constructor(id: string) {
        this._campaignId = id;
        this.campaignModel = new CampaignViewModel(id);
    }

    get checkboxIsNotReleaseModel() { return this._checkboxIsNotReleasedModel; }
    public get searchedSong() { return this._searchedSong.value; }
    get isAdmin() { return AppViewModel.Instance.userRole.isAdmin; }
    get isLoading() { return this._isLoading.value; }

    onChangeLink = async (link: string) => {
        if (!link.match(LinksRequestDto.LinkRegex)) {
            return;
        }

        const trackInfo = await this._getSpotifyTrackInfo(link);
        this._searchedSong.setValue(trackInfo);
    };

    public addPlaylist = async (data: CampaignPlaylistsAddData) => {
        this._isLoading.setTrue();

        await AppController.Instance.Campaigns.addPlaylist(this._campaignId, data.playlist);

        this._isLoading.setFalse();

        this.addPlaylistModalModel.setFalse();
    };

    public deletePlaylist = async (playlist: string) => {
        this._isLoading.setTrue();

        await AppController.Instance.Campaigns.deletePlaylist(this._campaignId, playlist);

        this._isLoading.setFalse();
    };

    public onSubmitUpdateMainInfo = async (data: CampaignMainInfoUpdateData) => {
        this._isLoading.setTrue();

        await AppController.Instance.Campaigns.updateCampaignMainInfo(this._campaignId, data);

        this._isLoading.setFalse();
        this.mainInfoModalModel.setFalse();
    };

    public pushCopiedNotification = () => {
        AppViewModel.Instance.notifications.setNotification('Link copied');
    };

    public onSubmitUpdateSongInfo = async (data: ArtistInfoData | AssetLinkData) => {
        this._isLoading.setTrue();

        if ('link' in data && data.link.length > 0) {
            if (!this._searchedSong.value) {
                const song = await this._getSpotifyTrackInfo(data.link);

                this._searchedSong.setValue(song);
            }

            const args = {
                assetName: this._searchedSong.value.title,
                authorName: this._searchedSong.value.artist,
                assetLink: this._searchedSong.value.link,
                releaseDate: this.campaignModel.releaseDate || null,
            };

            await AppController.Instance.Campaigns.updateAsset(this._campaignId, args);
        } else {
            const { artistName, songName, releaseDate } = data as ArtistInfoData;
            const args = {
                assetName: songName,
                authorName: artistName,
                assetLink: this.campaignModel.assetLink || null,
                releaseDate,
            };

            await AppController.Instance.Campaigns.updateAsset(this._campaignId, args);
        }

        this._isLoading.setFalse();
        this._searchedSong.reset();
        this.songModalModel.setFalse();
    };

    public onDeleteAssetLink = async () => {
        this._isLoading.setTrue();

        const args = {
            assetName: this.campaignModel.assetName,
            authorName: this.campaignModel.authorName,
            assetLink: null,
            releaseDate: this.campaignModel.releaseDate,
        };

        await AppController.Instance.Campaigns.updateAsset(this._campaignId, args);

        this._isLoading.setFalse();
        this.songModalModel.setFalse();
    };

    private async _getSpotifyTrackInfo(link: string) {
        const trackInfo = await AppController.Instance.Products.getSpotifyLink(link);

        logger.log('Received track info:', link, '=>', trackInfo);
        if (!trackInfo) {
            return null;
        }

        const artists = trackInfo.artists.map(a => a.name).join(' & ');
        const coverUrl = trackInfo.coverArt ? trackInfo.coverArt?.sources[0].url : trackInfo.visualIdentity?.image[0].url;

        return {
            title: trackInfo.name,
            fullTitle: `${artists} - ${trackInfo.name}`,
            artist: artists,
            link: link,
            logo: coverUrl,
        };
    }

}
